import { Eye, EyeOff } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat, NumericFormatProps } from "react-number-format";

import { cn } from "~/utils/cn";

type CustomInputProps = Omit<
  NumericFormatProps,
  "thousandSeparator" | "decimalSeparator" | "displayType" | "fixedDecimalScale" | "decimalScale" | "suffix"
> & {
  thousandSeparator?: string;
  decimalSeparator?: string;
  displayType?: "text" | "input";
  fixedDecimalScale?: boolean;
  decimalScale?: number;
  suffix?: string;
};


export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  rootClassName?: string;
}

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  ({
    thousandSeparator = ".",
    decimalSeparator = ",",
    fixedDecimalScale = true,
    decimalScale = 2,
    suffix = " €",
    displayType = "text",
    value,
    ...props
  }, ref) => {
    return (
      <NumericFormat
      {...props}
      getInputRef={ref}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      fixedDecimalScale={fixedDecimalScale}
      decimalScale={decimalScale}
      suffix={suffix}
      displayType={displayType}
      value={value}
    />
    );
  }
);


const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, rootClassName, ...props }, ref) => {
    const { i18n } = useTranslation();
    const [isVisible, setIsVisible] = React.useState<boolean | undefined>(
      undefined
    );
    const Icon = isVisible ? EyeOff : Eye;
    const togglePasswordVisibility = () => {
      setIsVisible(isVisible === undefined ? true : !isVisible);
    };

    return (
      <div className={cn("relative", rootClassName)}>
        <input
          className={cn(
            "flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
            className
          )}
          ref={ref}
          {...props}
          type={
            props.type === "password" && isVisible === true
              ? "text"
              : props.type
          }
          lang={i18n.language}
        />
        {props.type === "password" && (
          <Icon
            className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={togglePasswordVisibility}
          />
        )}
      </div>
    );
  }
);
Input.displayName = "Input";
CustomInput.displayName = "CustomInput";

export { CustomInput };
export { Input };
